// libraries
import React from 'react';
import Backbone from 'backbone';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';

// components
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Flexbox,
    Spinner,
    Tooltip,
    HelpIcon,
    AdaptiveTooltip
} from 'ui-connect';
import Translate from 'components/Translate';
import SyncNotification from './SyncNotification';

// redux
import {
    fetchRegisteredDeviceList,
    getRegisteredDeviceList,
    isRegisteredDeviceListFetching
} from '../../reducers/device/registeredDeviceList';
import { sendPaceProBandToDevice } from '../../reducers/pacePro/paceProBand';
import { postDeviceMessageViewed } from '../../actions/deviceMessageActions';
import { sendPowerGuidanceToDevice } from 'actions/powerGuidanceActions';
import { fetchCourseByCourseId, fetchSystemPreference } from '../../actions'; // Clean up ticket: CA-176058 - Remove fetchSystemPreference
import { getSystemPreferenceValue, getSystemPreferenceIsFetching } from 'reducers/system/systemPreferences'; // Clean up ticket: CA-176058
import {
    coursePlannerRoleKey, // Clean up ticket: CA-176058
    lapByCoursePointsRoleKey, // Clean up ticket: CA-185392
    harmonyConnectAppRoleKey // Cleanup ticket: CA-191687
} from 'models/preferences/PreferencesMapping';
import viewerSocialProfile from 'models/user/ViewerSocialProfile'; // Clean up ticket: CA-176058

// constants
import Types from '../../backbone/pages/workouts/util/Types';
import PoolWorkoutOptions from 'WorkoutPage/constants/PoolWorkoutOptions';
import { LEGACY_MAX_STEPS, POOL_SWIM_PARTIAL_SUPPORT_URL } from 'WorkoutPage/constants/WorkoutConstants';

// utilities
import GCSyncUtil from 'utils/GCSyncUtil';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import * as sendTypes from './SendToDeviceUtils';
import ViewerUserPreferences from '../../backbone/models/user/ViewerUserPreferences';
import { workoutHasStepsThatMeetFunctionCondition } from '../../utils/workout/WorkoutUtil';
import { getTotalWorkoutStepsCount, searchStepsRecursively } from 'WorkoutPage/utils/WorkoutUtil';
import { getFaqLink } from 'utils/LinksUtil';
import { courseHasLapByCoursePoints, courseHasPointWithData } from 'Courses/Utils';
import { applyHarmonyConnectCustomization } from 'utils/harmonyConnectApp/harmonyConnectAppUtil.js';

// styles
import styles from './NewSendToDevice.module.css';

const mapStateToProps = (state, props) => ({
    registeredDeviceList: getRegisteredDeviceList(state),
    isRegisteredDeviceListFetching: isRegisteredDeviceListFetching(state),
    coursePlannerRole: getSystemPreferenceValue(state, coursePlannerRoleKey), // Clean up ticket: CA-176058
    coursePlannerRoleFetching: getSystemPreferenceIsFetching(state), // Clean up ticket: CA-176058
    lapByCoursePointsRole: getSystemPreferenceValue(state, lapByCoursePointsRoleKey), // Clean up ticket: CA-185392
    lapByCoursePointsRoleFetching: getSystemPreferenceIsFetching(state), // Clean up ticket: CA-185392
    harmonyConnectAppRole: getSystemPreferenceValue(state, harmonyConnectAppRoleKey), // Cleanup ticket: CA-191687
    harmonyConnectAppRoleFetching: getSystemPreferenceIsFetching(state) // Cleanup ticket: CA-191687
});

const mapDispatchToProps = {
    fetchRegisteredDeviceList,
    sendPaceProBandToDevice,
    sendPowerGuidanceToDevice,
    fetchSystemPreference // Clean up ticket: CA-176058
};

class NewSendToDeviceModal extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            needHelp: false,
            sendToDevice: false,
            syncMessage: false,
            launchGE: ViewerUserPreferences.getLaunchGEFromCookies(),
            sendButtonDisabled: true,
            showNotification: false,
            showPopup: true,
            courseDetails: null
        };
        this.paragraphRef = React.createRef();
    }

    componentDidMount() {
        const {
            fetchRegisteredDeviceList,
            fetchSystemPreference,
            coursePlannerRole, // Clean up ticket: CA-176058
            lapByCoursePointsRole, // Clean up ticket: CA-185392
            harmonyConnectAppRole // Cleanup ticket: CA-191687
        } = this.props;

        // Clean up ticket: CA-176058
        fetchRegisteredDeviceList();
        if (!coursePlannerRole) {
            fetchSystemPreference(coursePlannerRoleKey);
        }

        // Clean up ticket: CA-185392
        if (!lapByCoursePointsRole) {
            fetchSystemPreference(lapByCoursePointsRoleKey);
        }

        // Cleanup ticket: CA-191687
        if (!harmonyConnectAppRole) {
            fetchSystemPreference(harmonyConnectAppRoleKey);
        }
    }

    componentDidUpdate() {
        const { coursePoints, sendType, courseId, isFromTrails } = this.props;

        if (this.paragraphRef) {
            const gcLink = document.querySelector('#clickGC');
            const geLink = document.querySelector('#clickGE');
            if (gcLink) {
                gcLink.addEventListener('click', this.onGarminConnectHelpItemClicked);
            }
            if (geLink) {
                geLink.addEventListener('click', this.onGarminExpressHelpItemClicked);
            }
        }

        // Make a call for coursePoints when a user wants to send it to their device if coursePoints wasn't already passed in
        if (
            sendType === sendTypes.SEND_TO_DEVICE_TYPE_COURSE &&
            courseId &&
            !coursePoints &&
            !this.state.courseDetails &&
            !isFromTrails
        ) {
            fetchCourseByCourseId(courseId).then((response) => {
                if (response && response.data) {
                    this.setState({
                        courseDetails: response.data
                    });
                }
            });
        }
    }

    componentWillUnmount() {
        const gcLink = document.querySelector('#clickGC');
        const geLink = document.querySelector('#clickGE');
        if (gcLink) {
            gcLink.removeEventListener('click', this.onGarminConnectHelpItemClicked);
        }
        if (geLink) {
            geLink.removeEventListener('click', this.onGarminExpressHelpItemClicked);
        }
    }

    onLaunchGE = () => {
        this.setState({ launchGE: !this.state.launchGE });
    };

    onDeviceChecked = () => {
        this.checkedDevice = this.compatibleDevice.filter((obj) => document.getElementById(obj.deviceId).checked);
        if (this.checkedDevice.length > 0) {
            this.setState({ sendButtonDisabled: false });
        } else {
            this.setState({ sendButtonDisabled: true });
        }
    };

    onCloseModalConfirm = () => {
        const { onClose } = this.props;
        if (this.isSendToDevice) {
            this.showNotification(true);
            this.updateNotificationMessage(this.messageKey, this.deviceList);
        }
        ViewerUserPreferences.setLaunchGECookies(this.state.launchGE);
        this.isSendToDevice = false;
        if (onClose) {
            onClose();
        } else {
            this.setState({
                showPopup: false
            });
        }

        if (this.state.courseDetails) {
            this.setState({
                courseDetails: null
            });
        }
    };

    openModalConfirm = () => {
        const { open } = this.props;
        if (typeof open !== 'undefined') {
            return open;
        } else {
            return this.state.showPopup;
        }
    };

    /*
     * sendToDeviceBtn - Sends deviceId, and paceBandId to api
     * Note: API will add this to the message queue on their side
     */
    sendToDeviceBtn = () => {
        const { sendType, onSendToDeviceButtonClicked } = this.props;
        let selectedDeviceId = null;
        let deviceMessage = [];
        this.messageKey = '';
        this.deviceList = [];

        if (this.compatibleDevice.length === 1) {
            selectedDeviceId = this.compatibleDevice[0].deviceId;
            this.processSendToDevicePayload(selectedDeviceId, sendType, deviceMessage, this.compatibleDevice[0]);
            this.deviceList.push(this.compatibleDevice[0].displayName);
            this.messageKey = 'send_to_device_to_single_device';
        } else {
            this.checkedDevice = this.compatibleDevice.filter((obj) => document.getElementById(obj.deviceId).checked);
            for (var i = 0; i < this.checkedDevice.length; i++) {
                selectedDeviceId = this.checkedDevice[i].deviceId;
                this.processSendToDevicePayload(selectedDeviceId, sendType, deviceMessage, this.checkedDevice[i]);
                this.deviceList.push(this.checkedDevice[i].displayName);
            }
            if (this.checkedDevice.length === 1) {
                this.messageKey = 'send_to_device_to_single_device';
            } else if (this.checkedDevice.length >= 2) {
                this.messageKey = 'send_to_device_to_mutiple_device';
            }
        }
        if (deviceMessage.length) {
            postDeviceMessageViewed(deviceMessage);
        }
        this.isSendToDevice = true;
        this.pushSendToDeviceAnalyticsEvents();
        if (document.getElementById('launchGE').checked) {
            GCSyncUtil.openAgent(selectedDeviceId);
        }
        if (onSendToDeviceButtonClicked && typeof onSendToDeviceButtonClicked === 'function') {
            onSendToDeviceButtonClicked(selectedDeviceId);
        }
        this.onCloseModalConfirm();
    };

    processSendToDevicePayload(selectedDeviceId, sendType, deviceMessage, selectedDevice) {
        const {
            segmentDetails,
            sendPaceProBandToDevice,
            workoutId,
            courseId,
            activityId,
            messageName,
            powerGuidanceId,
            sendPowerGuidanceToDevice,
            eventId,
            eventOrganizer,
            shareableEventUuid,
            completeWorkoutData
        } = this.props;

        let payload = {};
        let exportType = null;
        if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_PACEPRO) {
            payload = {
                deviceId: selectedDeviceId,
                paceBandId: this.props.paceBandId
            };
            sendPaceProBandToDevice(payload);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_SEGMENT) {
            payload = {
                deviceId: selectedDeviceId,
                messageUrl: 'course-service/leaderboard/fit/' + segmentDetails.uuid,
                messageType: 'segment-leaders',
                messageName: segmentDetails.name,
                groupName: 'Segments',
                priority: 0,
                fileType: 'FIT',
                metaDataId: null
            };
            deviceMessage.push(payload);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_WORKOUT) {
            exportType = selectedDevice.workoutFileType !== null ? selectedDevice.workoutFileType : 'FIT';
            payload = {
                deviceId: selectedDeviceId,
                messageUrl:
                    'workout-service/workout/' +
                    exportType +
                    '/' +
                    workoutId +
                    (!selectedDevice?.swimWorkout2Capable &&
                    completeWorkoutData?.sportType?.sportTypeKey === Types.SportTypes.Swimming
                        ? `?deviceId=${selectedDeviceId}`
                        : ''),
                messageType: 'workouts',
                messageName: messageName,
                groupName: null,
                priority: 1,
                fileType: exportType,
                metaDataId: workoutId
            };
            deviceMessage.push(payload);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_COURSE) {
            exportType = selectedDevice.courseFileType !== null ? selectedDevice.courseFileType : 'fit';
            payload = {
                deviceId: selectedDeviceId,
                messageUrl:
                    'course-service/course/' +
                    exportType.toLowerCase() +
                    '/' +
                    courseId +
                    '/' +
                    selectedDeviceId +
                    '?elevation=true',
                messageType: 'courses',
                messageName: messageName,
                groupName: null,
                priority: 0,
                fileType: exportType,
                metaDataId: courseId
            };
            deviceMessage.push(payload);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_PERSONAL_RECORD) {
            payload = {
                deviceId: selectedDeviceId,
                messageUrl: 'personalrecord-service/personalrecord/FIT/',
                messageType: 'personal-records',
                messageName: messageName,
                groupName: null,
                priority: 1,
                fileType: 'FIT',
                metaDataId: null
            };
            deviceMessage.push(payload);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_ACTIVITY) {
            exportType = selectedDevice.courseFileType !== null ? selectedDevice.courseFileType : 'fit';
            payload = {
                deviceId: selectedDeviceId,
                messageUrl:
                    'course-service/course/copyFromActivity/' +
                    exportType.toLowerCase() +
                    '/' +
                    activityId +
                    '/' +
                    selectedDeviceId +
                    '?elevation=true',
                messageType: 'courses',
                messageName: messageName,
                groupName: null,
                priority: 0,
                fileType: exportType,
                metaDataId: activityId
            };
            deviceMessage.push(payload);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_POWER_GUIDANCE) {
            let payload = {
                deviceId: selectedDevice.deviceId,
                powerGuidanceId: powerGuidanceId
            };
            sendPowerGuidanceToDevice(payload);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_SPORTING_EVENT) {
            payload = {
                deviceId: selectedDeviceId,
                messageUrl: eventOrganizer
                    ? `calendar-service/event/fit/${eventId}`
                    : `calendar-service/event/${shareableEventUuid}/shareable/fit`,
                messageType: 'sporting-event',
                messageName: messageName,
                groupName: null,
                priority: 0,
                fileType: 'FIT',
                metaDataId: eventId
            };
            deviceMessage.push(payload);
        }
    }

    loadingSpinner() {
        return (
            <Flexbox container center>
                <Spinner />
            </Flexbox>
        );
    }

    render() {
        return (
            <React.Fragment>
                <Dialog open={this.openModalConfirm()} onClose={this.onCloseModalConfirm}>
                    <DialogTitle>
                        <Translate ns="course" content="send_to_device" />
                    </DialogTitle>
                    {this.getContent()}
                </Dialog>
                {this.getNotification()}
            </React.Fragment>
        );
    }

    getContent() {
        const { sendType, registeredDeviceList, isRegisteredDeviceListFetching, additionalWorkoutFilter } = this.props;

        if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_PACEPRO) {
            this.filterDevicesForGcj02Case('pacebandCapable');
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_SEGMENT) {
            this.compatibleDevice = registeredDeviceList.filter((obj) => obj.segmentCapable);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_WORKOUT) {
            this.compatibleDevice = registeredDeviceList.filter((obj) => obj.workoutCapable);
            if (additionalWorkoutFilter) {
                this.compatibleDevice = registeredDeviceList.filter(
                    (obj) => obj.workoutCapable && obj[additionalWorkoutFilter]
                );
            } else {
                this.compatibleDevice = registeredDeviceList.filter((obj) => obj.workoutCapable);
            }
            this.compatibleDevice = this.filterWorkoutsWithTooManySteps(this.compatibleDevice);

            if (this.isSpecialWorkout()) {
                this.initialDeviceListForSpecialWorkout();
            }
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_COURSE) {
            this.filterDevicesForGcj02Case('courseCapable');
            this.initDeviceListsForCourses();
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_PERSONAL_RECORD) {
            this.compatibleDevice = registeredDeviceList.filter((obj) => obj.personalRecordCapable);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_ACTIVITY) {
            this.compatibleDevice = registeredDeviceList.filter((obj) => obj.courseCapable);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_POWER_GUIDANCE) {
            this.filterDevicesForGcj02Case('powerGuidanceCapable');
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_SPORTING_EVENT) {
            this.filterDevicesForGcj02Case('sportingEventCapable');
        }

        if (isRegisteredDeviceListFetching) {
            return this.loadingSpinner();
        }

        if (!this.compatibleDevice || this.compatibleDevice.length < 1) {
            return (
                <React.Fragment>
                    <DialogContent>
                        <div>
                            <p>
                                <Translate ns="device" content="send_to_device_no_devices" />
                            </p>
                        </div>
                        {this.getNoteMessage()}
                    </DialogContent>
                </React.Fragment>
            );
        }

        return (
            <React.Fragment>
                <DialogContent className={styles.dialogBody}>
                    <div>
                        <p>
                            {this.compatibleDevice.length === 1 ? (
                                <Translate ns="device" content="send_to_device_your_device" />
                            ) : (
                                <Translate ns="device" content="send_to_device_which_device" />
                            )}
                        </p>
                    </div>
                    {this.getDeviceList()}
                    {this.getNoteMessage()}
                </DialogContent>
                <DialogActions>
                    {/** Hide "Launch Garmin Express" checkbox when GCW is lanuched by harmony connect APP  */
                    !applyHarmonyConnectCustomization(this.hasHarmonyConnectAppRole()) && (
                        <span className={'pull-left ' + styles.launchLable}>
                            <label htmlFor="launchGE">
                                <input
                                    type="checkbox"
                                    id="launchGE"
                                    checked={this.state.launchGE}
                                    onChange={this.onLaunchGE}
                                />
                                <span className={styles.launchText}>
                                    <Translate ns="device" content="send_to_device_launch_ge" />
                                </span>
                            </label>
                        </span>
                    )}
                    <button
                        className={'btn btn-secondary cancel ' + styles.cancelBtn}
                        onClick={this.onCloseModalConfirm}
                    >
                        <Translate ns="common" content="button_cancel" />
                    </button>
                    <button
                        className="btn btn-confirm"
                        onClick={this.sendToDeviceBtn}
                        disabled={this.compatibleDevice.length > 1 && this.state.sendButtonDisabled}
                    >
                        {<Translate ns="personal-record-api" content="pr.button.send.label" />}
                    </button>
                </DialogActions>
            </React.Fragment>
        );
    }

    /**
     * Description: Filter out devices that don't support more than LEGACY_MAX_STEPS=50
     *   do not filter out devices for yoga workouts because they have their own max steps
     * @param {Array} deviceList
     * @returns {Array}
     */
    filterWorkoutsWithTooManySteps(deviceList) {
        const { completeWorkoutData } = this.props;
        let newDeviceList = deviceList;
        const totalSteps = getTotalWorkoutStepsCount(completeWorkoutData.workoutSegments);

        // yoga workouts support 250 steps, all others support less
        if (completeWorkoutData.sportType.sportTypeKey !== Types.SportTypes.Yoga && totalSteps > LEGACY_MAX_STEPS) {
            newDeviceList = deviceList.filter((obj) => obj.enhancedWorkoutStepCapable);
        }

        return newDeviceList;
    }

    filterDevicesForGcj02Case(requiredCapable) {
        const { courseOriginalCoordinateSystem, courseId, registeredDeviceList, isFromTrails } = this.props;

        if (!courseOriginalCoordinateSystem) {
            if (courseId && courseId > 0 && !isFromTrails) {
                fetchCourseByCourseId(courseId).then((response) => {
                    if (
                        response &&
                        response.data &&
                        response.data.originalCoordinateSystem ===
                            sendTypes.SEND_TO_DEVICE_COURSE_COORDINATE_SYSTEM_GCJ02
                    ) {
                        this.compatibleDevice = registeredDeviceList.filter(
                            (obj) => obj[requiredCapable] && obj.gcj02CourseCapable
                        );
                    } else {
                        this.compatibleDevice = registeredDeviceList.filter((obj) => obj[requiredCapable]);
                    }
                });
            } else {
                this.compatibleDevice = registeredDeviceList.filter((obj) => obj[requiredCapable]);
            }
        } else {
            if (courseOriginalCoordinateSystem === sendTypes.SEND_TO_DEVICE_COURSE_COORDINATE_SYSTEM_GCJ02) {
                this.compatibleDevice = registeredDeviceList.filter(
                    (obj) => obj[requiredCapable] && obj.gcj02CourseCapable
                );
            } else {
                this.compatibleDevice = registeredDeviceList.filter((obj) => obj[requiredCapable]);
            }
        }
    }

    getNoteMessage() {
        const { t } = this.props;

        return (
            <div
                className={styles.deviceNote}
                ref={this.paragraphRef}
                dangerouslySetInnerHTML={{
                    __html: t('device:send_to_device_note_2', {
                        0:
                            `<a class="colored" href="${getFaqLink(
                                '8CBmYmJHUr36wvJ6AktXFA'
                            )}" target="_blank" id="clickGC"> ` +
                            t('send_to_device:gc') +
                            '</a>',
                        1:
                            `<a class="colored" href="${getFaqLink(
                                'eagCIGaxGr1eDoN2yOW2b8'
                            )}" target="_blank" id="clickGE">` +
                            t('device:device.garmin.express') +
                            '</a>',
                        interpolation: { escapeValue: false }
                    })
                }}
            />
        );
    }

    isSpecialWorkout() {
        const { completeWorkoutData } = this.props;
        return (
            completeWorkoutData &&
            (this.workoutHasSecondaryTarget() ||
                this.workoutHasPowerCurve() ||
                this.workoutHasBenchmarkScaledExercise() ||
                this.workoutHasAudioNotes() ||
                this.workoutHasPoolSwim2Data() ||
                this.workoutHasSkipLastRepeatStep())
        );
    }

    /**
     * Description: Get course's points. They can come from the points passed in or fall back on requesting the
     *  full course details
     * @returns {Array}
     */
    getCoursePoints() {
        const { coursePoints } = this.props;

        return coursePoints?.length > 0 ? coursePoints : this?.state?.courseDetails?.coursePoints;
    }

    /**
     * Description: Determine if a link to support page should show when pool swim workout has partially compatible features
     *
     * @returns {boolean}
     */
    showPoolSwimPartiallyCompatibleLink() {
        // CA-169978: clean up poolSwim2 feature flag
        const { showPoolSwim2, completeWorkoutData } = this.props;
        const isPoolSwimWorkout = completeWorkoutData?.sportType?.sportTypeKey === Types.SportTypes.Swimming;

        // CA-169978: clean up poolSwim2 feature flag
        return (
            showPoolSwim2 &&
            isPoolSwimWorkout &&
            (this.workoutHasPoolSwim2Data() || this.workoutHasSkipLastRepeatStep())
        );
    }

    // Clean up ticket: CA-176058
    showCoursePlanner() {
        const { coursePlannerRole, coursePlannerRoleFetching } = this.props;

        return (
            !coursePlannerRoleFetching && coursePlannerRole !== null && viewerSocialProfile?.hasRole(coursePlannerRole)
        );
    }

    // Clean up ticket: CA-185392
    showLBCP() {
        const { lapByCoursePointsRole, lapByCoursePointsRoleFetching } = this.props;

        return (
            !lapByCoursePointsRoleFetching &&
            lapByCoursePointsRole !== null &&
            viewerSocialProfile?.hasRole(lapByCoursePointsRole)
        );
    }

    // Cleanup ticket: CA-191687
    hasHarmonyConnectAppRole() {
        const { harmonyConnectAppRole, harmonyConnectAppRoleFetching } = this.props;

        return (
            !harmonyConnectAppRoleFetching &&
            harmonyConnectAppRole !== null &&
            viewerSocialProfile?.hasRole(harmonyConnectAppRole)
        );
    }

    getDeviceList() {
        const { t, sendType } = this.props;

        if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_WORKOUT && this.isSpecialWorkout()) {
            if (this.fullCompatibleDeviceList.length === 0 && this.partiallyCompatibleDeviceList.length > 0) {
                return this.partiallyCompatibleDeviceList.length > 1 ? (
                    <>
                        <div className={styles.deviceListTitle}>
                            <Translate ns="device" content="send_to_device_partially_compatible_devices" />
                            {this.showPoolSwimPartiallyCompatibleLink() && (
                                <Tooltip title={t('common:learn_more')} position={'top'}>
                                    <span className={styles.deviceHelpIcon}>
                                        <HelpIcon
                                            onClick={(e) => {
                                                e.preventDefault();
                                                window.open(POOL_SWIM_PARTIAL_SUPPORT_URL, '_blank');
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                        {this.getPartiallyCompatibleDeviceList()}
                    </>
                ) : (
                    <div className={styles.device} key={this.partiallyCompatibleDeviceList[0].deviceId}>
                        <img
                            className={styles.deviceImage}
                            src={this.partiallyCompatibleDeviceList[0].imageUrl}
                            alt={this.partiallyCompatibleDeviceList[0].displayName}
                        />
                        <h3 className={styles.deviceName}>{this.partiallyCompatibleDeviceList[0].displayName}</h3>
                        <AdaptiveTooltip
                            title={this.partiallyCompatibleTooltipTitle(this.partiallyCompatibleDeviceList[0])}
                            position={'topRight'}
                            rootMargin={'-210px 0px 0px 0px'}
                            customContentStyles={{ maxWidth: '170px' }}
                        >
                            <span className={styles.deviceHelpIcon}>
                                <HelpIcon />
                            </span>
                        </AdaptiveTooltip>
                    </div>
                );
            } else if (this.fullCompatibleDeviceList.length > 0 && this.partiallyCompatibleDeviceList.length > 0) {
                return (
                    <React.Fragment>
                        <div className={styles.deviceListTitle}>
                            <Translate ns="device" content="send_to_device_fully_compatible_devices" />
                        </div>
                        {this.getFullyCompatibleDeviceList()}
                        <div className={styles.deviceListTitle}>
                            <Translate ns="device" content="send_to_device_partially_compatible_devices" />
                            {this.showPoolSwimPartiallyCompatibleLink() && (
                                <Tooltip title={t('common:learn_more')} position={'top'}>
                                    <span className={styles.deviceHelpIcon}>
                                        <HelpIcon
                                            onClick={(e) => {
                                                e.preventDefault();
                                                window.open(POOL_SWIM_PARTIAL_SUPPORT_URL, '_blank');
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                            )}
                        </div>
                        {this.getPartiallyCompatibleDeviceList()}
                    </React.Fragment>
                );
            }
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_COURSE) {
            // When there is a mix of fully and partially capable devices for courses with course points
            if (this.fullCompatibleDeviceList.length > 0 && this.partiallyCompatibleDeviceList.length > 0) {
                return (
                    <React.Fragment>
                        <div className={styles.deviceListTitle}>
                            <Translate ns="device" content="send_to_device_fully_compatible_devices" />
                        </div>
                        {this.getFullyCompatibleDeviceList()}
                        <div className={styles.deviceListTitle}>
                            <Translate ns="device" content="send_to_device_partially_compatible_devices" />
                        </div>
                        {this.getPartiallyCompatibleDeviceList()}
                    </React.Fragment>
                );
            }
            // When there are only partially capable devices
            // Note, when there are only fully capable devices, use the default behavior
            else if (this.fullCompatibleDeviceList.length === 0 && this.partiallyCompatibleDeviceList.length > 0) {
                return (
                    <React.Fragment>
                        <div className={styles.deviceListTitle}>
                            <Translate ns="device" content="send_to_device_partially_compatible_devices" />
                        </div>
                        {this.getPartiallyCompatibleDeviceList()}
                    </React.Fragment>
                );
            }
        }

        // Default behavior
        return this.compatibleDevice.length > 1
            ? this.compatibleDevice.map((obj) => (
                  <div className={styles.device} key={obj.deviceId}>
                      <label htmlFor={obj.deviceId} className={styles.deviceLabel}>
                          <input
                              type="checkbox"
                              value={obj.displayName}
                              id={obj.deviceId}
                              onClick={this.onDeviceChecked}
                          />
                          <img className={styles.deviceImage} src={obj.imageUrl} alt={obj.displayName} />
                          <h3 className={styles.deviceName}>{obj.displayName}</h3>
                      </label>
                  </div>
              ))
            : this.compatibleDevice.map((obj) => (
                  <div className={styles.device} key={obj.deviceId}>
                      <img className={styles.deviceImage} src={obj.imageUrl} alt={obj.displayName} />
                      <h3 className={styles.deviceName}>{obj.displayName}</h3>
                  </div>
              ));
    }

    getFullyCompatibleDeviceList() {
        return this.fullCompatibleDeviceList.map((obj) => (
            <div className={styles.device} key={obj.deviceId}>
                <label htmlFor={obj.deviceId} className={styles.deviceLabel}>
                    <input type="checkbox" value={obj.displayName} id={obj.deviceId} onClick={this.onDeviceChecked} />
                    <img className={styles.deviceImage} src={obj.imageUrl} alt={obj.displayName} />
                    <h3 className={styles.deviceName}>{obj.displayName}</h3>
                </label>
            </div>
        ));
    }

    getPartiallyCompatibleDeviceList() {
        return this.partiallyCompatibleDeviceList.map((obj) => (
            <div className={styles.device} key={obj.deviceId}>
                <label htmlFor={obj.deviceId} className={styles.deviceLabel}>
                    <input type="checkbox" value={obj.displayName} id={obj.deviceId} onClick={this.onDeviceChecked} />
                    <img className={styles.deviceImage} src={obj.imageUrl} alt={obj.displayName} />
                    <h3 className={styles.deviceName}>{obj.displayName}</h3>
                </label>
                <AdaptiveTooltip
                    title={this.partiallyCompatibleTooltipTitle(obj)}
                    position={'topRight'}
                    rootMargin={'-210px 0px 0px 0px'}
                    customContentStyles={{ maxWidth: '170px' }}
                >
                    <span className={styles.deviceHelpIcon}>
                        <HelpIcon />
                    </span>
                </AdaptiveTooltip>
            </div>
        ));
    }

    partiallyCompatibleTooltipTitle(obj) {
        const { sendType } = this.props;

        if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_WORKOUT) {
            return this.partiallyCompatibleTooltipTitleForWorkouts(obj);
        } else if (sendType === sendTypes.SEND_TO_DEVICE_TYPE_COURSE) {
            return this.partiallyCompatibleTooltipTitleForCourses(obj);
        }
    }

    partiallyCompatibleTooltipTitleForWorkouts(obj) {
        const { t } = this.props;

        // CA-169978: clean up poolSwim2 feature flag
        const { completeWorkoutData, showPoolSwim2 } = this.props;
        let str = '';

        // CA-169978: clean up poolSwim2 feature flag
        if (
            showPoolSwim2 &&
            completeWorkoutData.sportType.sportTypeKey === Types.SportTypes.Swimming &&
            (this.workoutHasPoolSwim2Data() || this.workoutHasSkipLastRepeatStep())
        ) {
            str = this.multiplePatiallyCompatibleSwimFeatures(obj);
        } else if (showPoolSwim2 && this.workoutHasSkipLastRepeatStep()) {
            // CA-169978: clean up poolSwim2 feature flag
            str = t('workout:workout_device_does_not_support_one_feature', {
                0: obj.displayName,
                1: t('workout:label_skip_last_rest')
            });
        } else if (this.workoutHasAudioNotes()) {
            str = t('workout:audio_notes_not_available', { 0: obj.displayName });
        } else if (this.workoutHasSecondaryTarget()) {
            str = t('device:send_to_device_partially_compatible_warning', {
                0: obj.displayName,
                1: t('workout:secondary_targets_capable')
            });
        } else if (this.workoutHasPowerCurve()) {
            str = t('device:send_to_device_partially_compatible_warning', {
                0: obj.displayName,
                1: t('workout:power_curve_capable')
            });
        } else if (this.workoutHasBenchmarkScaledExercise()) {
            str = t('device:send_to_device_partially_compatible_warning', { 0: obj.displayName, 1: '' });
        }
        return str;
    }

    partiallyCompatibleTooltipTitleForCourses(obj) {
        // intentionally prioritize LBCP over Course Planner. Potential future improvement to make a list of partially compatible features
        if (this.showLBCP() && this.courseHasLBCPData() && !obj?.lapByCoursePointsCapable) {
            // Clean up ticket: CA-185392
            return <Translate ns="course" content="lbcp_partially_compatible" params={{ 0: obj.displayName }} />;
        } else if (this.showCoursePlanner() && this.courseHasCoursePlannerData() && !obj?.coursePlannerCapable) {
            // Clean up ticket: CA-176058
            return (
                <Translate
                    ns="course_planner"
                    content="send_to_device_partially_compatible_warning_for_courses"
                    params={{ 0: obj.displayName }}
                />
            );
        }
    }

    /**
     * Description: Get the tooltip string for devices that are partially compatible with poolSwim2 features like
     * unspecified pool size, skip last repeat step, drill types, etc...
     * @param {object} obj
     * @returns {string}
     */
    multiplePatiallyCompatibleSwimFeatures(obj) {
        const { t } = this.props;

        const { completeWorkoutData } = this.props;
        let partiallyCompatibleFeatures = {
            unspecifiedPoolSize: {
                exists: completeWorkoutData.poolLength === null,
                label: t('workout:unspecified_pool_sizes')
            },
            skipLastRestStep: {
                exists: false,
                label: t('workout:label_skip_last_rest')
            },
            kickDrillTypes: {
                exists: false,
                label: t('workout:kick_drill_types')
            },
            pullDrillTypes: {
                exists: false,
                label: t('workout:pull_drill_types')
            },
            poolSwimIntensityTarget: {
                exists: false,
                label: t('workout:intensity_targets')
            },
            IMByRoundStrokeType: {
                exists: false,
                label: t('workout:IM_by_round_stroke_types')
            },
            RIMOStrokeType: {
                exists: false,
                label: t('workout:RIMO_stroke_types')
            }
        };
        let tooltipString = '';

        completeWorkoutData.workoutSegments.map((segment) => {
            searchStepsRecursively(segment.workoutSteps, (step) => {
                // set flags based on step data
                if (!!step?.skipLastRestStep) {
                    partiallyCompatibleFeatures.skipLastRestStep.exists = true;
                }
                if (step?.drillType?.drillTypeKey === PoolWorkoutOptions.DrillTypes.Pull) {
                    partiallyCompatibleFeatures.pullDrillTypes.exists = true;
                }
                if (step?.drillType?.drillTypeKey === PoolWorkoutOptions.DrillTypes.Kick) {
                    partiallyCompatibleFeatures.kickDrillTypes.exists = true;
                }
                if (
                    !!step.secondaryTargetType &&
                    step.secondaryTargetType?.workoutTargetTypeKey !== Types.TargetTypes.No_Target
                ) {
                    // pool swim uses secondary targets as primary targets because device uses primary target fields for drill types
                    partiallyCompatibleFeatures.poolSwimIntensityTarget.exists = true;
                }
                if (step?.strokeType?.strokeTypeKey === PoolWorkoutOptions.StrokeTypes.IMByRound) {
                    partiallyCompatibleFeatures.IMByRoundStrokeType.exists = true;
                }
                if (step?.strokeType?.strokeTypeKey === PoolWorkoutOptions.StrokeTypes.ReverseIMByRound) {
                    partiallyCompatibleFeatures.RIMOStrokeType.exists = true;
                }
            });
        });

        const partialFeatures = Object.keys(partiallyCompatibleFeatures).filter(
            (feature) => !!partiallyCompatibleFeatures[feature].exists
        );

        if (partialFeatures.length > 3) {
            tooltipString = t('workout:workout_device_does_not_support_more_than_three_features', {
                0: obj.displayName
            });
        } else if (partialFeatures.length === 3) {
            tooltipString = t('workout:workout_device_does_not_support_three_features', {
                0: obj.displayName,
                1: partiallyCompatibleFeatures[partialFeatures[0]].label,
                2: partiallyCompatibleFeatures[partialFeatures[1]].label,
                3: partiallyCompatibleFeatures[partialFeatures[2]].label
            });
        } else if (partialFeatures.length === 2) {
            tooltipString = t('workout:workout_device_does_not_support_two_features', {
                0: obj.displayName,
                1: partiallyCompatibleFeatures[partialFeatures[0]].label,
                2: partiallyCompatibleFeatures[partialFeatures[1]].label
            });
        } else if (partialFeatures.length === 1) {
            tooltipString = t('workout:workout_device_does_not_support_one_feature', {
                0: obj.displayName,
                1: partiallyCompatibleFeatures[partialFeatures[0]].label
            });
        }

        return tooltipString;
    }

    /**
     * Description: Filter devices into fully/partially compatible lists
     *   Use this.compatibleDevice instead of this.registeredDeviceList as the device list to filter because the calling component
     *   can pass in additionalWorkoutFilter to do a pre-filter of devices like (don't even show devices that don't support swim workouts)
     */
    initialDeviceListForSpecialWorkout() {
        // CA-169978: clean up poolSwim2 feature flag
        const { completeWorkoutData, showPoolSwim2 } = this.props;
        this.fullCompatibleDeviceList = [];
        this.partiallyCompatibleDeviceList = [];

        // pool swim workouts have their own special logic for filtering partially/fully compatible devices
        // one reason is pool swim uses secondary target fields as primary targets, unlike other workout types
        if (completeWorkoutData.sportType.sportTypeKey === Types.SportTypes.Swimming) {
            // CA-169978: clean up poolSwim2 feature flag - remove this if block including the return statement
            if (!showPoolSwim2) {
                return;
            }

            // no filtering required if the pool swim workout doesn't have any partially compatible features in it
            if (!this.workoutHasPoolSwim2Data() && !this.workoutHasSkipLastRepeatStep()) {
                return;
            }

            // filter fully compatible device list based on what features the workout has
            this.fullCompatibleDeviceList = this.compatibleDevice.filter((device) => {
                let fullyCompatible = true;

                // if workout has pool swim 2 data and device is not compatible set not fully compatible
                if (this.workoutHasPoolSwim2Data() && !device.swimWorkout2Capable) {
                    fullyCompatible = false;
                }

                // if workout has skip last step data and device is not compatible set not fully compatible
                if (this.workoutHasSkipLastRepeatStep() && !device.skipLastWorkoutStepInRepeatBlockCapable) {
                    fullyCompatible = false;
                }

                return fullyCompatible;
            });

            // filter partially compatible device list
            this.partiallyCompatibleDeviceList = this.compatibleDevice.filter((device) => {
                let partiallyCompatible = false;

                // if workout has pool swim 2 data and device is not compatible set partially compatible
                if (this.workoutHasPoolSwim2Data() && !device.swimWorkout2Capable) {
                    partiallyCompatible = true;
                }

                // if workout has skip last step data and device is not compatible set partially compatible
                if (this.workoutHasSkipLastRepeatStep() && !device.skipLastWorkoutStepInRepeatBlockCapable) {
                    partiallyCompatible = true;
                }

                return partiallyCompatible;
            });
        } else if (showPoolSwim2 && this.workoutHasSkipLastRepeatStep()) {
            // skip last repeat step for non-poolSwim workouts
            this.fullCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && obj.skipLastWorkoutStepInRepeatBlockCapable
            );
            this.partiallyCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && !obj.skipLastWorkoutStepInRepeatBlockCapable
            );
        } else if (this.workoutHasAudioNotes()) {
            this.fullCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && obj.workoutAudioPromptsCapable
            );
            this.partiallyCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && !obj.workoutAudioPromptsCapable
            );
        } else if (this.workoutHasSecondaryTarget()) {
            this.fullCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && obj.secondaryWorkoutStepTargetCapable
            );
            this.partiallyCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && !obj.secondaryWorkoutStepTargetCapable
            );
        } else if (this.workoutHasPowerCurve()) {
            this.fullCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && obj.powerCurveCapable
            );
            this.partiallyCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && !obj.powerCurveCapable
            );
        } else if (this.workoutHasBenchmarkScaledExercise()) {
            this.fullCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && obj.benchmarkExerciseCapable
            );
            this.partiallyCompatibleDeviceList = this.compatibleDevice.filter(
                (obj) => obj.workoutCapable && !obj.benchmarkExerciseCapable
            );
        }
        if (this.fullCompatibleDeviceList.length > 0 && this.partiallyCompatibleDeviceList.length === 0) {
            this.compatibleDevice = this.fullCompatibleDeviceList;
        } else if (this.fullCompatibleDeviceList.length === 0 && this.partiallyCompatibleDeviceList.length > 0) {
            this.compatibleDevice = this.partiallyCompatibleDeviceList;
        } else if (this.fullCompatibleDeviceList.length > 0 && this.partiallyCompatibleDeviceList.length > 0) {
            this.compatibleDevice = this.fullCompatibleDeviceList.concat(this.partiallyCompatibleDeviceList);
        }
    }

    /**
     * Description: Filter devices into fully/partially compatible lists for sending course to device
     */
    initDeviceListsForCourses() {
        this.fullCompatibleDeviceList = [];
        this.partiallyCompatibleDeviceList = [];

        const coursePlannerDataExists = this.courseHasCoursePlannerData();
        const lapByCoursePointsDataExists = this.courseHasLBCPData();

        this.fullCompatibleDeviceList = this.compatibleDevice.filter((obj) => {
            // Clean up ticket: CA-176058
            const isCoursePlannerFullyCompatible =
                this.showCoursePlanner() && coursePlannerDataExists
                    ? obj.courseCapable && obj.coursePlannerCapable
                    : obj.courseCapable;
            // Clean up ticket: CA-185392
            const isLBCPFulyCompatible =
                this.showLBCP() && lapByCoursePointsDataExists
                    ? obj.courseCapable && obj.lapByCoursePointsCapable
                    : obj.courseCapable;

            return isCoursePlannerFullyCompatible && isLBCPFulyCompatible;
        });
        this.partiallyCompatibleDeviceList = this.compatibleDevice.filter((obj) => {
            // Clean up ticket: CA-176058
            const isCoursePlannerPartiallyCompatible =
                this.showCoursePlanner() && coursePlannerDataExists && obj.courseCapable && !obj.coursePlannerCapable;
            // Clean up ticket: CA-185392
            const isLBCPPartiallyCompatible =
                this.showLBCP() && lapByCoursePointsDataExists && obj.courseCapable && !obj.lapByCoursePointsCapable;

            return isCoursePlannerPartiallyCompatible || isLBCPPartiallyCompatible;
        });
    }

    /**
     * Description: Return true if the course has any Course Planner data in the course points
     * @returns {boolean}
     */
    courseHasCoursePlannerData() {
        // wrap course points in an object because they are either passed in from the course page or the entire course data is fetched from the courses list page
        // the courses list page API endpoint doesn't provide the points so they get fetched in this component
        const wrappedCourseData = { coursePoints: this.getCoursePoints() };

        return (
            courseHasPointWithData(wrappedCourseData, 'note') ||
            courseHasPointWithData(wrappedCourseData, 'cutoffDuration') ||
            courseHasPointWithData(wrappedCourseData, 'restDuration')
        );
    }

    /**
     * Description: Return true if the course has Lap by Course Points on it
     * @returns {boolean}
     */
    courseHasLBCPData() {
        // wrap course points in an object because they are either passed in from the course page or the entire course data is fetched from the courses list page
        // the courses list page API endpoint doesn't provide the points so they get fetched in this component
        const wrappedCourseData = { coursePoints: this.getCoursePoints() };

        // since this is just one function calling a single other function we don't really need it but I have put it here as a best practice for any dev adding future features
        // because they often have more than a singe check and it is nice in this file to have a single has<Feature>Data() function to be called where it's needed
        return courseHasLapByCoursePoints(wrappedCourseData);
    }

    workoutHasAudioNotes() {
        const { completeWorkoutData } = this.props;

        if (!!completeWorkoutData) {
            const hasOverviewAudioNote = !!this.getWorkoutDataField('workoutAudioNoteUuid');
            const workoutSegments = this.getWorkoutDataField('workoutSegments');
            const workoutSteps = workoutSegments && workoutSegments.length > 0 ? workoutSegments[0].workoutSteps : [];
            const hasStepAudioNote = workoutSteps.some((step) => step.stepAudioNoteUuid);

            return hasOverviewAudioNote || hasStepAudioNote;
        }
        return false;
    }

    /**
     * Description: Does the workout contain a repeat step with skipLastStep set in any segment
     * @returns {boolean}
     */
    workoutHasSkipLastRepeatStep() {
        const { completeWorkoutData } = this.props;
        let skipLastReatStepExists = false;

        completeWorkoutData.workoutSegments.map((segment) => {
            searchStepsRecursively(segment.workoutSteps, (step) => {
                if (step?.skipLastRestStep) {
                    skipLastReatStepExists = true;
                }
            });
        });

        return skipLastReatStepExists;
    }

    /**
     * Description: Does workout contain any steps with poolSwim2 data in any segment
     * @returns {boolean}
     */
    workoutHasPoolSwim2Data() {
        const { completeWorkoutData } = this.props;
        let hasPoolSwim2Data = false;

        // if workout is not pool swim ignore any pool swim specific logic
        if (completeWorkoutData.sportType.sportTypeKey !== Types.SportTypes.Swimming) {
            return false;
        }

        completeWorkoutData.workoutSegments.map((segment) => {
            searchStepsRecursively(segment.workoutSteps, (step) => {
                if (
                    completeWorkoutData?.poolLength === null ||
                    step?.drillType?.drillTypeKey === PoolWorkoutOptions.DrillTypes.Pull ||
                    step?.drillType?.drillTypeKey === PoolWorkoutOptions.DrillTypes.Kick ||
                    (!!step.secondaryTargetType &&
                        step.secondaryTargetType?.workoutTargetTypeKey !== Types.TargetTypes.No_Target) ||
                    step?.strokeType?.strokeTypeKey === PoolWorkoutOptions.StrokeTypes.IMByRound ||
                    step?.strokeType?.strokeTypeKey === PoolWorkoutOptions.StrokeTypes.ReverseIMByRound
                ) {
                    hasPoolSwim2Data = true;
                }
            });
        });

        return hasPoolSwim2Data;
    }

    /**
     * Description: Does workout contain any steps with a secondary target in any segment
     * @returns {boolean}
     */
    workoutHasSecondaryTarget() {
        const { completeWorkoutData } = this.props;

        let hasSecondaryTarget = false;

        completeWorkoutData.workoutSegments.map((segment) => {
            searchStepsRecursively(segment.workoutSteps, (step) => {
                if (
                    step.secondaryTargetType &&
                    step.secondaryTargetType.workoutTargetTypeKey &&
                    step.secondaryTargetType.workoutTargetTypeKey !== Types.TargetTypes.No_Target
                ) {
                    hasSecondaryTarget = true;
                }
            });
        });

        return hasSecondaryTarget;
    }

    /**
     * Description: Does workout contain any steps with power curve intensity/secondary target in any segment
     * @returns {boolean}
     */
    workoutHasPowerCurve() {
        const { completeWorkoutData } = this.props;
        let hasPowerCurveValue = false;

        completeWorkoutData.workoutSegments.map((segment) => {
            searchStepsRecursively(segment.workoutSteps, (step) => {
                if (
                    !!(
                        step.secondaryTargetType &&
                        step.secondaryTargetType.workoutTargetTypeKey &&
                        step.secondaryTargetType.workoutTargetTypeKey === Types.TargetTypes.Power_Curve
                    ) ||
                    !!(
                        step.targetType &&
                        step.targetType.workoutTargetTypeKey &&
                        step.targetType.workoutTargetTypeKey === Types.TargetTypes.Power_Curve
                    )
                ) {
                    hasPowerCurveValue = true;
                }
            });
        });

        //if value is found this will always be the top parent
        return hasPowerCurveValue;
    }

    workoutHasBenchmarkScaledExercise() {
        const { completeWorkoutData } = this.props;

        let hasBenchmarkScaledExercise = false;
        if (completeWorkoutData && typeof completeWorkoutData === 'object') {
            const workoutSegments = this.getWorkoutDataField('workoutSegments');
            const functionToApply = (step) => step.benchmarkKey || step.benchmarkPercentage > 0;

            hasBenchmarkScaledExercise = workoutHasStepsThatMeetFunctionCondition(workoutSegments, functionToApply);
        }
        return hasBenchmarkScaledExercise;
    }

    /**
     * @param {string} fieldName
     */
    getWorkoutDataField(fieldName) {
        const { completeWorkoutData } = this.props;
        // react passes a json object and backbone passes in a model you have to use .get() the field
        return (
            completeWorkoutData[fieldName] ||
            (completeWorkoutData instanceof Backbone.Model && completeWorkoutData.get(fieldName))
        );
    }

    getNotification() {
        const { t, sendType, isFromTrails } = this.props;

        let itemName = '';

        switch (sendType) {
            case sendTypes.SEND_TO_DEVICE_TYPE_WORKOUT:
                itemName = t('send_to_device:queue.workout');
                break;
            case sendTypes.SEND_TO_DEVICE_TYPE_ACTIVITY:
            case sendTypes.SEND_TO_DEVICE_TYPE_COURSE:
                itemName = t('activity-api:course');
                break;
            case sendTypes.SEND_TO_DEVICE_TYPE_SEGMENT:
                itemName = t('send_to_device:queue.segment');
                break;
            case sendTypes.SEND_TO_DEVICE_TYPE_PACEPRO:
                itemName = t('widgets:label_pace_pro');
                break;
            case sendTypes.SEND_TO_DEVICE_TYPE_PERSONAL_RECORD:
                itemName = t('personal-record-api:pr.label');
                break;
            case sendTypes.SEND_TO_DEVICE_TYPE_POWER_GUIDANCE:
                itemName = t('common:title_powerguide_widget');
                break;
            default:
                itemName = t('send_to_device:queue.workout');
                break;
        }

        return (
            this.state.showNotification && (
                <SyncNotification
                    messageKey={this.state.notificationMessageKey}
                    itemName={itemName.toLowerCase()}
                    autoClose={true}
                    isShow={this.state.showNotification}
                    onClose={() => {
                        this.setState({ showNotification: false });
                    }}
                    onLearnMoreHelpItemClicked={this.onLearnMoreHelpItemClicked.bind(this)}
                    zIndex={6}
                    isFromTrails={isFromTrails}
                />
            )
        );
    }

    pushSendToDeviceAnalyticsEvents() {
        const { sendType, sendToDeviceFrom } = this.props;
        let action =
            this.checkedDevice && this.checkedDevice.length >= 2 ? 'Sent Multiple Devices' : 'Sent Single Device';

        AnalyticsUtil.push({
            event: 'track_event',
            category: 'Send ' + sendType + ' To Device Events From ' + sendToDeviceFrom,
            action: action,
            label: 'Is Garmin Express Launched : ' + this.state.launchGE
        });
    }

    onGarminConnectHelpItemClicked = () => {
        this.pushSendToDeviceHelpItemsAnalyticsEvents('Garmin Connect');
    };

    onGarminExpressHelpItemClicked = () => {
        this.pushSendToDeviceHelpItemsAnalyticsEvents('Garmin Express');
    };

    onLearnMoreHelpItemClicked = () => {
        this.pushSendToDeviceHelpItemsAnalyticsEvents('Learn More');
    };

    pushSendToDeviceHelpItemsAnalyticsEvents(item) {
        AnalyticsUtil.push({
            event: 'track_event',
            category: 'Send To Device Events',
            action: 'Help Link : ' + item + ' Clicked'
        });
    }

    showNotification = (show) => {
        if (show) {
            this.setState({
                showNotification: true
            });
        }
    };

    updateNotificationMessage = (notificationMessageKey, deviceList) => {
        this.setState({
            notificationMessageKey: notificationMessageKey,
            deviceList: deviceList
        });
    };
}

export default withTranslation([
    'device',
    'send_to_device',
    'workout',
    'activity-api',
    'common',
    'personal-record-api',
    'widgets',
    'activity-api',
    'trails'
])(connect(mapStateToProps, mapDispatchToProps)(NewSendToDeviceModal));
